
import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface RouteGuardOptions {
  redirectTo: string;
  shouldRedirect: () => boolean | Promise<boolean>;
  skipWhen?: () => boolean;
}

/**
 * A hook that guards routes based on custom logic
 * @param options Configuration options for the route guard
 * @returns An object containing the loading state
 */
const useRouteGuard = (options: RouteGuardOptions) => {
  const { redirectTo, shouldRedirect, skipWhen } = options;
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        // Skip the guard check if the skipWhen condition is true
        if (skipWhen && skipWhen()) {
          setLoading(false);
          return;
        }

        // Check if redirection is needed
        const needsRedirect = await shouldRedirect();
        
        if (needsRedirect) {
          // Store the attempted URL for possible redirection back after login
          if (location.pathname !== redirectTo) {
            sessionStorage.setItem('redirectAfterLogin', location.pathname + location.search);
          }
          navigate(redirectTo, { replace: true });
        }
        
        setLoading(false);
      } catch (error) {
        console.error("Error in route guard:", error);
        setLoading(false);
      }
    };

    checkAccess();
  }, [location.pathname, navigate, redirectTo, shouldRedirect, skipWhen, location.search]);

  return { loading };
};

export default useRouteGuard;
